import cx from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useSessionstorageState } from 'rooks';

import { Icon } from 'components/@tedui';
import { RadioButton } from 'components/@tedui/Atoms/RadioButton';
import Toggle from 'components/Toggle/Toggle';
import useVideoPlayerStore from 'components/video-player/store';

import { Text } from 'components/typography';
import SkipForward from 'icons/SkipForwardIcon';
import Speed from 'icons/SpeedIcon';
import { useAutoplayPreference } from 'lib/preferences/useAutoplayPreference';

const speeds = {
  '0.5x': 0.5,
  '0.75x': 0.75,
  Normal: 1,
  '1.25x': 1.25,
  '1.5x': 1.5
};

const AnimatedRadioButton = motion.create(RadioButton);

const SettingsPanel = ({ showSpeeds = true }): ReactElement => {
  const { onPlaybackRate, playbackRate, setShouldAutoplay } =
    useVideoPlayerStore(state => ({
      onPlaybackRate: state.onPlaybackRate,
      playbackRate: state.playbackRate,
      setShouldAutoplay: state.setShouldAutoplay
    }));

  const [autoplayPreference, setAutoplayPreference] = useAutoplayPreference();

  const [storedPlaybackRate, setStoredPlaybackRate] = useSessionstorageState(
    'playback_rate_state',
    playbackRate
  );

  const defaultSpeed = useMemo(
    () =>
      Object.entries(speeds).find(([, value]) => value === playbackRate)?.[0] ||
      'Normal',
    [playbackRate]
  );

  const [selected, setSelected] = useState(defaultSpeed);
  const [isCollapsed, setIsCollapsed] = useState(true);

  const toggleCollapse = useCallback(
    () => setIsCollapsed(!isCollapsed),
    [isCollapsed]
  );

  const closeCollapse = useCallback(() => setIsCollapsed(true), []);

  const handleChange = useCallback(
    (speedMultiplier, speedLabel) => {
      setSelected(speedLabel);
      closeCollapse();
      setStoredPlaybackRate(speedMultiplier);
      onPlaybackRate(speedMultiplier, true);
    },
    [onPlaybackRate, closeCollapse, setStoredPlaybackRate]
  );

  const toggleAutoplay = useCallback(() => {
    if (autoplayPreference === null) return;

    const newValue = !autoplayPreference;
    setAutoplayPreference(newValue);
    setShouldAutoplay(newValue);
  }, [autoplayPreference, setAutoplayPreference, setShouldAutoplay]);

  useEffect(() => {
    if (storedPlaybackRate !== playbackRate) {
      onPlaybackRate(storedPlaybackRate);
      const speedLabel =
        Object.keys(speeds).find(
          key => speeds[key as keyof typeof speeds] === storedPlaybackRate
        ) || 'Normal';
      setSelected(speedLabel);
    }
  }, [storedPlaybackRate, playbackRate, onPlaybackRate]);

  const height = useMemo(() => {
    if (!showSpeeds) return 35;
    return isCollapsed ? 65 : 165;
  }, [isCollapsed, showSpeeds]);

  return (
    <AnimatePresence presenceAffectsLayout mode="wait">
      <motion.div
        onMouseLeave={closeCollapse}
        animate={{
          height
        }}
        className={cx(
          'px-3 py-2 md:w-full md:rounded-sm sm-tui:max-h-1/2-screen sm-tui:rounded-none',
          {
            'min-w-44': !showSpeeds
          }
        )}
      >
        <div
          className={cx('flex items-center justify-between', {
            'mb-2': showSpeeds
          })}
        >
          <div className="flex flex-row items-center">
            <SkipForward className="mr-3 h-5 w-5" />
            <Text size="m" className="text-left">
              <FormattedMessage defaultMessage="Autoplay" />
            </Text>
          </div>
          <Toggle
            toggledColorDot="bg-white"
            isOn={autoplayPreference ?? false}
            handleToggle={toggleAutoplay}
          />
        </div>

        {showSpeeds && (
          <button
            type="button"
            className="mb-2 flex items-center justify-between border-none bg-transparent"
            onClick={toggleCollapse}
          >
            <div className="flex flex-row items-center">
              <Speed variant="default" className="mr-3 h-5 w-5" />
              <Text size="m" className="w-28 text-left">
                <FormattedMessage defaultMessage="Speed: " /> {selected}
              </Text>
            </div>
            <Icon
              className="ml-6 origin-center scale-150 text-tui-sm text-textPrimary-onLight"
              iconName={isCollapsed ? 'chevron-right' : 'chevron-up'}
            />
          </button>
        )}

        {!isCollapsed && (
          <motion.ul
            className="pl-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {Object.keys(speeds).map(label => (
              <AnimatedRadioButton
                name="sound_speed_settings"
                key={label}
                option={label}
                selected={label === selected}
                onChange={() =>
                  handleChange(speeds[label as keyof typeof speeds], label)
                }
              />
            ))}
          </motion.ul>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default SettingsPanel;
